import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { FC, SVGProps, useState } from "react";
import { LoaderFunction, useLoaderData, useNavigate } from "react-router-dom";

import {
  CURRENT_ORDER_QUERY_KEY,
  invalidateCurrentOrderDetails,
  useCurrentOrderDetails,
} from "@/modules/cart/useCartDetails";
import { ReferralRewardsCard } from "@/modules/referralRewards/ReferralRewardsCard/ReferralRewardsCard";
import { get, post } from "api";
import { DiscountUse, GetResult, Models } from "api/types";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import Left from "atoms/icon/icons/chevron_left.svg";
import RewardOpen from "atoms/icon/icons/ic_open_reward_small_ol.svg";
import Offers from "atoms/icon/icons/offers_nav.svg";
import QrCode from "atoms/icon/icons/qr_code.svg";
import Shop from "atoms/icon/icons/shop_nav.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import { BadgeTag } from "atoms/tag/BadgeTag";
import { useAdonisChat } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import { AdvisorCTA } from "modules/adonis/AdvisorCTA/AdvisorCTA";
import { Analytics, AnalyticsEvents } from "modules/analytics";
import { useUserPreferences } from "modules/user/useUser/useUser";
import TopNav from "molecules/navbars/TopNav";
import CampaignCard from "organisms/campaign-card/CampaignCard";
import { ItemAppliedToCartModal } from "organisms/item-applied-to-cart-modal/ItemAppliedToCartModal";
import { ItemNotAppliedToCartModal } from "organisms/item-not-applied-to-cart-modal/ItemNotAppliedToCartModal";
import ProductCarousel from "organisms/product-carousel/ProductCarousel";
import { PromotionDetails } from "organisms/promotion-details/PromotionDetails";
import { RedeemedFreePackageGroupModal } from "organisms/redeemed-free-package-group-modal/RedeemedFreePackageGroupModal";
import { ReferAFriend } from "organisms/refer-a-friend/ReferAFriend";
import { FreeServiceSelector } from "organisms/selection-modals/free-service-selector/FreeServiceSelector";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { getListingsWithDiscountInformation } from "services/ListingHelperService";
import { notFalsy, notNull } from "toolbox/Guards";
import { waitObj } from "toolbox/Promise";
import { FormattedString } from "utils/FormattedStrings";

/**
 * An information panel similar to a ProductCard, but which covers the entire
 * screen and allows for richer description formatting
 */
const InfoOverlay = (props: {
  header: string;
  imageSrc?: string | null;
  title: string;
  description: string;
  disclaimer?: string;
  primary?: string;
  showNavbar: boolean;
  isInStoreRedeemable?: boolean | null;
  isPromotionForFreeService?: boolean | null;
  expiry?: dayjs.Dayjs | null;
  onClose(): void;
  onButtonClick?(): void;
  primaryDisabled?: boolean;
  promotion?: Models<"PromotionModel">;
  inclusions?: Models<"InclusionListingsModel">;
}) => {
  const promotion = props.promotion;
  const promoCode = promotion?.promoCodeModel;
  const discount = promoCode?.discount ?? undefined;
  const showFreePackageRedeemed =
    props.isPromotionForFreeService && promotion?.redeemedOn !== null;
  const inclusions = props.inclusions;

  return (
    <div className="z-10 -m-px flex min-h-full flex-col bg-white pb-[safe-area-inset-bottom] sm:border">
      {props.showNavbar && (
        <div className="sticky top-0 z-20 flex items-center justify-between bg-white pt-[env(safe-area-inset-top)]">
          <IconButton svg={Left} style="iconOnly" onClick={props.onClose} />
          <div className="absolute left-1/2 -translate-x-1/2 text-center text-sub2 uppercase">
            {props.header}
          </div>
        </div>
      )}
      {props.imageSrc && (
        <div className="relative">
          {props.expiry && (
            <div className="absolute left-3 top-3">
              <BadgeTag color="alert">{`Expires ${props.expiry.fromNow()}`}</BadgeTag>
            </div>
          )}

          <img
            alt="Promotion image."
            className="aspect-video w-full object-cover"
            src={props.imageSrc}
          />
        </div>
      )}
      <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4 pb-[calc(10px_+_env(safe-area-inset-bottom))] text-primary">
        <h1 className="font-serif text-h1">{props.title}</h1>
        {props.description && (
          <FormattedString>{props.description}</FormattedString>
        )}
        {showFreePackageRedeemed && (
          <>
            <div className="mt-4 flex items-center justify-center rounded bg-two px-4 py-5">
              <div>
                <Icon svg={RewardOpen} size="small" color="brandColor" />
              </div>
              <div className="ml-4 text-bold1">
                Congratulations! This offer has already been redeemed
              </div>
            </div>
            <div className="py-4 text-center text-body1 text-primary">
              {"Redeemed"}:{" "}
              {dayjs(promotion?.redeemedOn).format("ddd, L - hh:mma")}
            </div>
          </>
        )}
        {promoCode && !showFreePackageRedeemed && (
          <PromotionDetails
            inclusions={inclusions}
            discount={discount}
            customDisclaimer={props.disclaimer}
            freePackageGroup={promotion.freePackageGroup ?? undefined}
          />
        )}
      </div>
      <div className="sticky bottom-[calc(59px_+_env(safe-area-inset-bottom)-1px)] w-full bg-white p-4 empty:hidden">
        {!props.isInStoreRedeemable && props.primary && (
          <Button
            fullWidth
            size="medium"
            onClick={props.onButtonClick}
            disabled={props.primaryDisabled}
          >
            {props.primary}
          </Button>
        )}
        {props.isInStoreRedeemable && (
          <Button
            fullWidth
            size="medium"
            onClick={props.onButtonClick}
            data-testid="button-redeem-offer"
          >
            <Icon svg={QrCode} size="xsmall" color="white" />
            <span className="ml-1">Redeem offer</span>
          </Button>
        )}
      </div>
    </div>
  );
};

const PromotionInfo = (props: {
  widgetId?: string | null;
  promotionId: string;
  showNavbar: boolean;
  cartDetails: Models<"ShopDisplayOrderResult"> | undefined;
  onNotAppliedToCartError: () => void;
  onAppliedToCart: (promotionTitle: string) => void;
}) => {
  const navigate = useNavigate();
  const { data: widget } = useQuery({
    queryKey: ["/widgets", { page: "DiscoverPage" }],
    queryFn: () => get("/widgets", { page: "DiscoverPage" }),
    select: (data) => data.find((w) => w.id === props.widgetId),
  });
  const { data: promotion, isLoading: isPromotionLoading } = useQuery({
    queryKey: ["/promotions/{promotionId}", props.promotionId],
    queryFn: () =>
      get("/promotions/{promotionId}", { promotionId: props.promotionId }),
  });
  const {
    data: promotionInclusions,
    isLoading: arePromotionInclusionsLoading,
  } = useQuery({
    queryKey: ["/promotions/{promotionId}/inclusions", promotion?.id],
    queryFn: () =>
      promotion?.id
        ? get("/promotions/{promotionId}/inclusions", {
            promotionId: promotion.id,
          })
        : Promise.resolve(undefined),
    enabled: !!promotion?.id,
  });
  const redeem = useMutation({
    mutationFn: () =>
      post("/promotions/{promotionId}/redeem", {
        promotionId: props.promotionId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["/promotions"]);
      invalidateCurrentOrderDetails();
      queryClient.invalidateQueries({ queryKey: ["/orders/current/summary"] });
      if (promotion?.isInStoreRedeemable) {
        navigate(`/account/offers/${props.promotionId}?claimed=1`);
      } else if (promotion) {
        props.onAppliedToCart(promotion.title);
      }
    },
    onError: (error) => {
      if (
        isAxiosError(error) &&
        error.response?.status === 400 &&
        error.response.data === "itemsInCartNotIncludedInDiscount"
      ) {
        props.onNotAppliedToCartError();
      }
    },
  });

  const applyPromotionToCart = useMutation({
    mutationFn: ({ promoCodeValue }: { promoCodeValue: string }) =>
      post("/orders/current/promoCode", {
        // @ts-expect-error body type is marked as `never` in the API, but it actually expects a string
        $data: promoCodeValue,
      }),
    onSuccess: async () => {
      await invalidateCurrentOrderDetails();
      queryClient.invalidateQueries({ queryKey: ["/orders/current/summary"] });
      if (promotion) {
        props.onAppliedToCart(promotion.title);
      }
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 400) {
        props.onNotAppliedToCartError();
      }
    },
  });

  const { data: cartDetails, isSuccess: cartDetailsIsSuccess } =
    useCurrentOrderDetails({
      initialData: props?.cartDetails,
      retry: (_: unknown, error: unknown) =>
        !(isAxiosError(error) && error.response?.status === 400),
    });

  const { data: authClaims } = useQuery({
    queryKey: ["/auth/claims"],
    queryFn: () => get("/auth/claims"),
  });

  const { data: listings } = useQuery({
    queryKey: ["/listings"],
    queryFn: () => get("/listings"),
  });

  const { data: memberships } = useQuery({
    queryKey: ["/memberships"],
    queryFn: () => get("/memberships"),
  });

  const [selectedInclusionsToRedeem, setSelectedInclusionsToRedeem] = useState<
    string[]
  >([]);

  const [showFreeServiceSelector, setShowFreeServiceSelector] = useState(false);

  const [showSuccessfulRedemptionModal, setShowSuccessfulRedemptionModal] =
    useState(false);

  const packagesFromListings = (listings ?? []).flatMap(
    (listing) => listing.packages
  );

  const itemsForSelection =
    promotion?.freePackageGroup?.freePackageGroupInclusions
      .filter(
        ({ membershipId, packageId }) =>
          membershipId !== null || packageId !== null
      )
      .map((inclusion) => {
        if (inclusion.membershipId) {
          const membership = memberships?.find(
            ({ id }) => id === inclusion.membershipId
          );
          return {
            name: membership?.name ?? "",
            quantity: inclusion.quantity,
            unitType: "month",
            id: inclusion.id,
          };
        }

        const listingPackage = packagesFromListings.find(
          ({ id }) => id === inclusion.packageId
        );
        const packageUnitType =
          inclusion.quantity < 2
            ? listingPackage?.packageUnitType.singularUnitType
            : listingPackage?.packageUnitType.pluralUnitType;
        return {
          name: listingPackage?.name ?? "",
          quantity: inclusion.quantity,
          unitType: packageUnitType ?? "",
          id: inclusion.id,
        };
      }) ?? [];

  const handleRedeemFreePackageGroupOffer = async (
    freePackageGroupInclusionIds: string[]
  ) => {
    if (!promotion?.freePackageGroup) return;
    setSelectedInclusionsToRedeem(freePackageGroupInclusionIds);
    await post("/free-package-groups/{freePackageGroupId}/redeem", {
      freePackageGroupId: promotion.freePackageGroup.id,
      freePackageGroupInclusionIds,
    });
  };

  const redeemFreePackageGroup = useMutation({
    mutationFn: handleRedeemFreePackageGroupOffer,
    onSuccess: async () => {
      // Revalidate the offer details query to update the offer status
      await queryClient.invalidateQueries([
        "/promotions/{promotionId}",
        props.promotionId,
      ]);
      await invalidateCurrentOrderDetails();

      // show success modal
      setShowSuccessfulRedemptionModal(true);
    },
    onSettled: async () => {
      if (showFreeServiceSelector) setShowFreeServiceSelector(false);
    },
  });

  const isAdminImpersonation = !!authClaims?.claims.adminUserId;

  if (!promotion || isPromotionLoading) {
    return null;
  }
  if (promotion.id && arePromotionInclusionsLoading) {
    return null;
  }

  const expiry = promotion.expiresOn ? dayjs(promotion.expiresOn) : null;

  const isPromotionAppliedToCart =
    cartDetailsIsSuccess &&
    cartDetails?.discountSummary?.discounts?.some(
      (d) => d.promoCodeId === promotion.promoCodeModel.id
    );
  const isDiscountMultiUse =
    promotion.promoCodeModel.discount?.useType === DiscountUse.Unlimited;

  const isPromotionForFreeService = promotion.freePackageGroup !== null;
  const hasGiftCardInclusions = !!promotionInclusions?.giftCards?.length;

  const onFreeServicesSelected = (selectedInclusions: string[]) => {
    redeemFreePackageGroup.mutate(selectedInclusions);
  };

  const getPrimaryButtonText = () => {
    if (isPromotionAppliedToCart) {
      return "Already applied in cart";
    }
    if (
      widget?.primaryText ||
      isPromotionForFreeService ||
      (promotion.isInStoreRedeemable && isAdminImpersonation)
    ) {
      return "Redeem offer";
    }
    return "Apply offer to cart";
  };

  const onRedeemFreePackageGroup = () => {
    if (!promotion.freePackageGroup?.freePackageGroupInclusions) {
      return;
    }
    const isChooseFrom = promotion.freePackageGroup.hasSelectableInclusions;
    if (isChooseFrom) {
      setShowFreeServiceSelector(true);
    } else {
      redeemFreePackageGroup.mutate(
        promotion.freePackageGroup?.freePackageGroupInclusions.map((i) => i.id)
      );
    }
  };

  const hasMembershipInclusions =
    !!promotion.freePackageGroup?.freePackageGroupInclusions.some(
      ({ membershipId }) => membershipId !== null
    );

  return (
    <>
      <InfoOverlay
        header="Learn More!"
        imageSrc={promotion.imageUrl}
        title={promotion.title}
        description={promotion.description}
        disclaimer={promotion.disclaimer || undefined}
        primary={getPrimaryButtonText()}
        primaryDisabled={
          arePromotionInclusionsLoading ||
          (hasGiftCardInclusions
            ? false
            : redeem.isLoading ||
              isPromotionAppliedToCart ||
              applyPromotionToCart.isLoading ||
              (isPromotionForFreeService && promotion.redeemedOn !== null))
        }
        showNavbar={props.showNavbar}
        isInStoreRedeemable={
          promotion.isInStoreRedeemable && !isAdminImpersonation
        }
        isPromotionForFreeService={isPromotionForFreeService}
        expiry={expiry}
        onButtonClick={() => {
          if (promotion.isInStoreRedeemable && !isAdminImpersonation) {
            navigate(`/scan-redeem-offer-in-location`, {
              state: {
                promotionId: promotion.id,
              },
            });
          } else if (
            !promotion.isInStoreRedeemable &&
            isPromotionForFreeService
          ) {
            onRedeemFreePackageGroup();
          } else {
            if (widget?.primaryUrl) open(widget.primaryUrl, "_blank");

            if (promotion.redeemedOn && isDiscountMultiUse) {
              applyPromotionToCart.mutate({
                promoCodeValue: promotion.promoCodeModel.code,
              });
            } else if (!redeem.isLoading) {
              redeem.mutate();
            }
          }
        }}
        onClose={() => navigate("")}
        promotion={promotion}
        inclusions={promotionInclusions}
      />

      {showFreeServiceSelector &&
        promotion.freePackageGroup?.hasSelectableInclusions &&
        !!promotion.freePackageGroup?.numberOfSelections && (
          <FreeServiceSelector
            open={showFreeServiceSelector}
            onClose={() => setShowFreeServiceSelector(false)}
            size="fullscreen"
            title={"Redeem Your Offer"}
            items={itemsForSelection}
            numberOfSelections={promotion.freePackageGroup.numberOfSelections}
            onFirstButtonClick={onFreeServicesSelected}
            firstButtonLabel={"Confirm"}
            onSecondButtonClick={() => setShowFreeServiceSelector(false)}
            secondButtonLabel={"Cancel"}
            itemType={"offer"}
          />
        )}
      {showSuccessfulRedemptionModal && (
        <RedeemedFreePackageGroupModal
          itemType="offer"
          itemName={promotion.title}
          onClose={() => setShowSuccessfulRedemptionModal(false)}
          redeemedInclusions={itemsForSelection.filter((inclusion) =>
            selectedInclusionsToRedeem.includes(inclusion.id)
          )}
          hasMembershipInclusions={hasMembershipInclusions}
        />
      )}
    </>
  );
};

const CustomWidgetInfo = (props: { widgetId: string; showNavbar: boolean }) => {
  const navigate = useNavigate();
  const { data: widget } = useQuery({
    queryKey: ["/widgets", { page: "DiscoverPage" }],
    queryFn: () => get("/widgets", { page: "DiscoverPage" }),
    select: (data) => data.find((w) => w.id === props.widgetId),
  });

  const imageSrc = widget?.widgetData[0].imageUrl;
  const title = widget?.widgetData[0].title;
  const description = widget?.widgetData[0].description;
  const primary = widget?.primaryUrl ? widget.primaryText : undefined;

  if (!title || !description) return null;

  return (
    <InfoOverlay
      header="Learn More"
      imageSrc={imageSrc}
      title={title}
      description={description}
      primary={primary ?? undefined}
      showNavbar={props.showNavbar}
      onButtonClick={() => {
        if (widget.primaryUrl) open(widget.primaryUrl, "_blank");
      }}
      onClose={() => navigate("")}
    />
  );
};

const RepeatOfferInfo = (props: { showNavbar: boolean }) => {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["/offers"],
    queryFn: () => get("/offers"),
  });

  if (!data) return null;

  return (
    <InfoOverlay
      header="Learn More"
      imageSrc={data.imageUrl}
      title={data.title}
      description={data.description}
      primary="Browse Shop"
      showNavbar={props.showNavbar}
      onButtonClick={() => navigate("/shop")}
      onClose={() => navigate("")}
    />
  );
};

const ICONS: Record<Models<"WidgetIcon">, FC<SVGProps<SVGSVGElement>>> = {
  Shop,
  Offers,
};

type WidgetProps = {
  widget: Models<"WidgetModel">;
  marketplaceEnabled?: boolean;
};

const PromotionCard = (
  props: WidgetProps & {
    promotionId: string;
    onPromotionNotAppliedToCartError?: () => void;
    onPromotionAppliedToCart?: (promotionTitle: string) => void;
    cartDetails: Models<"ShopDisplayOrderResult"> | undefined;
  }
) => {
  const navigate = useNavigate();

  const { data: isPromotionAvailable } = useQuery({
    queryKey: ["/promotions"],
    queryFn: () => get("/promotions"),
    select: (data) => !!data.find((p) => p.id === props.promotionId),
    staleTime: 1000 * 60 * 2,
  });

  const { data } = useQuery({
    queryKey: ["/promotions/{promotionId}", props.promotionId],
    queryFn: () =>
      get("/promotions/{promotionId}", {
        promotionId: props.promotionId,
      }),
    enabled: isPromotionAvailable || false,
  });

  const { data: cartDetails, isSuccess: cartDetailsIsSuccess } =
    useCurrentOrderDetails({
      initialData: props?.cartDetails,
      retry: (_: unknown, error: unknown) =>
        !(isAxiosError(error) && error.response?.status === 400),
    });

  const redeem = useMutation({
    mutationFn: () =>
      post("/promotions/{promotionId}/redeem", {
        promotionId: props.promotionId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["/promotions"]);
      invalidateCurrentOrderDetails();
      queryClient.invalidateQueries({ queryKey: ["/orders/current/summary"] });
      if (data?.isInStoreRedeemable) {
        navigate(`/account/offers/${props.promotionId}?claimed=1`);
      } else if (data && props.onPromotionAppliedToCart) {
        props.onPromotionAppliedToCart(data.title);
      }
    },
    onError: (error) => {
      if (
        props.onPromotionNotAppliedToCartError &&
        isAxiosError(error) &&
        error.response?.status === 400 &&
        error.response.data === "itemsInCartNotIncludedInDiscount"
      ) {
        props.onPromotionNotAppliedToCartError();
      }
    },
  });

  const { data: authClaims } = useQuery({
    queryKey: ["/auth/claims"],
    queryFn: () => get("/auth/claims"),
  });

  const applyPromotionToCart = useMutation({
    mutationFn: ({ promoCodeValue }: { promoCodeValue: string }) =>
      post("/orders/current/promoCode", {
        // @ts-expect-error body type is marked as `never` in the API, but it actually expects a string
        $data: promoCodeValue,
      }),
    onSuccess: () => {
      invalidateCurrentOrderDetails();
      queryClient.invalidateQueries({ queryKey: ["/orders/current/summary"] });
      if (data && props.onPromotionAppliedToCart) {
        props.onPromotionAppliedToCart(data.title);
      }
    },
    onError: (error) => {
      if (
        props.onPromotionNotAppliedToCartError &&
        isAxiosError(error) &&
        error.response?.status === 400
      ) {
        props.onPromotionNotAppliedToCartError();
      }
    },
  });

  const isAdminImpersonation = !!authClaims?.claims.adminUserId;

  const isDiscountMultiUse =
    data?.promoCodeModel.discount?.useType === DiscountUse.Unlimited;
  const {
    data: promotionCardInclusions,
    isLoading: arePromotionCardInclusionsLoading,
  } = useQuery({
    queryKey: ["/promotions/{promotionId}/inclusions", data?.id],
    queryFn: () =>
      data?.id
        ? get("/promotions/{promotionId}/inclusions", {
            promotionId: data.id,
          })
        : Promise.resolve(undefined),
    enabled: !!data?.id,
    staleTime: 1000 * 60,
  });
  const hasGiftCardInclusions = !!promotionCardInclusions?.giftCards?.length;

  if (!data || (data.redeemedOn && !isDiscountMultiUse)) return null;

  const tag = data.expiresOn
    ? { alert: `Expires ${dayjs(data.expiresOn).fromNow()}` }
    : null;

  const isPromotionAppliedToCart =
    cartDetailsIsSuccess &&
    cartDetails?.discountSummary?.discounts?.some(
      (d) => d.promoCodeId === data.promoCodeModel.id
    );

  const isPromotionForFreeService = data.freePackageGroup !== null;

  const getPrimaryButtonText = () => {
    if (isPromotionForFreeService) return "Learn More";
    if (isPromotionAppliedToCart) return "Already applied in cart";
    if (data.isInStoreRedeemable) return "Redeem offer";
    else return props.widget.primaryText ?? "Apply offer to cart";
  };

  const handlePrimaryButtonClick = () => {
    if (!data.isInStoreRedeemable || isAdminImpersonation) {
      if (isPromotionForFreeService) {
        navigate(`?widget=${props.widget.id}&promotion=${data.id}`);
        return;
      }
      if (props.widget.primaryUrl) open(props.widget.primaryUrl, "_blank");
      if (data.redeemedOn && isDiscountMultiUse) {
        applyPromotionToCart.mutate({
          promoCodeValue: data.promoCodeModel.code,
        });
      } else if (!redeem.isLoading) {
        redeem.mutate();
      }
    } else {
      navigate(`/scan-redeem-offer-in-location`, {
        state: {
          promotionId: data.id,
        },
      });
    }
  };

  return (
    <CampaignCard
      imageSrc={data.imageUrl}
      tag={tag}
      svg={props.widget.icon && ICONS[props.widget.icon]}
      buttonSvg={
        data.isInStoreRedeemable && !isAdminImpersonation ? QrCode : null
      }
      title={props.widget.title || data.title}
      description={props.widget.description || data.shortDescription}
      primaryDisabled={
        arePromotionCardInclusionsLoading ||
        (!hasGiftCardInclusions &&
          (redeem.isLoading ||
            isPromotionAppliedToCart ||
            applyPromotionToCart.isLoading))
      }
      primary={getPrimaryButtonText()}
      secondary={isPromotionForFreeService ? undefined : "Learn More"}
      onButtonClick={(btn) => {
        if (btn === "primary") {
          handlePrimaryButtonClick();
        } else if (btn === "secondary") {
          navigate(`?widget=${props.widget.id}&promotion=${data.id}`);
        }
      }}
    />
  );
};

const ListingCard = (props: WidgetProps & { listingId: string }) => {
  const navigate = useNavigate();
  const { data, isSuccess } = useQuery({
    queryKey: ["/listings", props.listingId],
    queryFn: () => get("/listings/{id}", { id: props.listingId }),
    staleTime: 1000 * 60,
  });

  if (!isSuccess || !data) return null;

  return (
    <CampaignCard
      imageSrc={data.imageUrl}
      svg={props.widget.icon && ICONS[props.widget.icon]}
      title={props.widget.title || data.title}
      description={props.widget.description || data.description}
      primary={props.widget.primaryText || "Learn More"}
      onButtonClick={() => navigate("/pdp/package/" + data.id)}
    />
  );
};

const MembershipCard = (props: WidgetProps & { membershipId: string }) => {
  const navigate = useNavigate();
  const { data, isSuccess } = useQuery({
    queryKey: ["/memberships", props.membershipId],
    queryFn: () =>
      get("/memberships/{membershipId}", { membershipId: props.membershipId }),
  });

  if (!isSuccess || !data) return null;

  return (
    <CampaignCard
      imageSrc={data.imageUrl}
      svg={props.widget.icon && ICONS[props.widget.icon]}
      title={props.widget.title || data.name}
      description={props.widget.description || data.description || ""}
      primary={props.widget.primaryText || "See All Benefits"}
      onButtonClick={() => navigate("/pdp/membership/" + data.id)}
    />
  );
};

const GenericCard = (props: {
  widget: Models<"WidgetModel">;
  showNavbar: boolean;
}) => {
  const navigate = useNavigate();
  const title = props.widget.title;
  const description = props.widget.description;
  const imageUrl = props.widget.widgetData[0]?.imageUrl;

  const primaryText = props.widget.primaryText ?? "Learn More";
  const primaryUrl = props.widget.primaryUrl;

  const secondaryText = primaryText && primaryUrl ? "Learn More" : undefined;

  if (!title || !description) return null;

  let search = `?widget=${props.widget.id}`;
  if (!props.showNavbar) {
    search = search + "&isMobileApp=true";
  }

  return (
    <CampaignCard
      svg={props.widget.icon && ICONS[props.widget.icon]}
      title={title}
      description={description}
      imageSrc={imageUrl}
      primary={primaryText}
      secondary={secondaryText}
      onButtonClick={(btn) => {
        if (btn === "primary" && !primaryUrl) {
          navigate(search);
        } else if (btn === "primary" && primaryUrl) {
          // If primaryUrl includes a phone number, navigate in the same tab.
          // If not, open it in a new tab.
          if (primaryUrl.startsWith("tel:")) {
            window.location.href = primaryUrl;
          } else {
            open(primaryUrl, "_blank");
          }
        } else if (btn === "secondary") {
          navigate(search);
        }
      }}
    />
  );
};

const DiscoverCard = (props: {
  widget: Models<"WidgetModel">;
  showNavbar: boolean;
  marketplaceEnabled?: boolean;
  onPromotionNotAppliedToCartError?: () => void;
  onPromotionAppliedToCart?: (promotionTitle: string) => void;
  cartDetails: Models<"ShopDisplayOrderResult"> | undefined;
}) => {
  const data = props.widget.widgetData[0];

  if (data?.promotionId) {
    return (
      <PromotionCard
        widget={props.widget}
        promotionId={data.promotionId}
        onPromotionNotAppliedToCartError={
          props.onPromotionNotAppliedToCartError
        }
        onPromotionAppliedToCart={props.onPromotionAppliedToCart}
        cartDetails={props.cartDetails}
      />
    );
  } else if (data?.packageListingId && props.marketplaceEnabled) {
    return (
      <ListingCard widget={props.widget} listingId={data.packageListingId} />
    );
  } else if (data?.membershipId && props.marketplaceEnabled) {
    return (
      <MembershipCard widget={props.widget} membershipId={data.membershipId} />
    );
  } else {
    return <GenericCard widget={props.widget} showNavbar={props.showNavbar} />;
  }
};

const DiscoverCarousel = (props: WidgetProps) => {
  const promotions = useQueries({
    queries: props.widget.widgetData.map((d) => ({
      enabled: !!d.promotionId,
      notifyOnChangeProps: ["data" as const],
      queryKey: ["/promotions"],
      queryFn: () => get("/promotions"),
      select: (data: GetResult<"/promotions">) => {
        const promotion = data.find((p) => p.id === d.promotionId);
        if (!promotion || promotion.redeemedOn) return;
        return {
          to: `?widget=${props.widget.id}&promotion=${promotion.id}`,
          tag: "Promotion",
          title: promotion.title,
          imageSrc: promotion.imageUrl ?? "",
          primary: "View Offer",
          alert:
            promotion.expiresOn &&
            `Expires ${dayjs(promotion.expiresOn).fromNow()}`,
        };
      },
      staleTime: 1000 * 60 * 2,
    })),
  });

  const listings = useQueries({
    queries: props.widget.widgetData.map((d) => ({
      enabled: !!d.packageListingId,
      queryKey: ["/listings", d.packageListingId],
      queryFn: () => get("/listings/{id}", { id: d.packageListingId! }),
      select: (data: GetResult<"/listings/{id}">) => ({
        to: "/pdp/package/" + data.id,
        tag: data.type,
        title: data.title,
        imageSrc: data.imageUrl ?? "",
        price: data.baseCost,
        memberPrice: data.membershipCost,
      }),
      staleTime: 1000 * 60,
    })),
  });

  const memberships = useQueries({
    queries: props.widget.widgetData.map((d) => ({
      enabled: !!d.membershipId,
      queryKey: ["/memberships", d.membershipId],
      queryFn: () =>
        get("/memberships/{membershipId}", { membershipId: d.membershipId! }),
      select: (data: GetResult<"/memberships/{membershipId}">) => ({
        to: "/pdp/membership/" + data.id,
        tag: "Membership",
        title: data.name,
        imageSrc: data.imageUrl ?? "",
        price: data.monthlyCost,
      }),
    })),
  });

  const title = props.widget.title;
  if (!title || !(promotions.length + listings.length)) return null;

  return (
    <ProductCarousel
      svg={props.widget.icon && ICONS[props.widget.icon]}
      title={title}
      description={props.widget.description}
      cards={props.widget.widgetData
        .map((d, i) => {
          if (d.promotionId) return promotions[i].data;
          if (d.packageListingId && props.marketplaceEnabled)
            return listings[i].data;
          if (d.membershipId && props.marketplaceEnabled)
            return memberships[i].data;
        })
        .filter(notNull)}
    />
  );
};

const VacationCard = (props: {
  widget: Models<"WidgetModel">;
  showNavbar: boolean;
}) => {
  const navigate = useNavigate();
  const { data, isSuccess } = useQuery(["/offers"], () => get("/offers"));

  if (!isSuccess || !data) return null;

  let search = `?repeatOffer=${data.id}`;
  if (!props.showNavbar) {
    search = search + "&isMobileApp=true";
  }

  return (
    <CampaignCard
      imageSrc={data.imageUrl}
      subtitle={props.widget.subtitle}
      title={props.widget.title || data.title}
      description={props.widget.description || data.shortDescription}
      primary={props.widget.primaryText || "Learn More"}
      onButtonClick={() => navigate(search)}
    />
  );
};

const TopSellingCarousel = (props: WidgetProps) => {
  const { data: shopDiscounts } = useQuery({
    queryKey: ["/shop/discounts"],
    queryFn: () => get("/shop/discounts"),
    staleTime: 1000 * 10,
  });
  const { data: customShopCategories } = useQuery({
    queryKey: ["/shop/custom-categories/listings"],
    queryFn: () => get("/shop/custom-categories/listings"),
    staleTime: 1000 * 10,
  });
  const { data: listings } = useQuery({
    queryKey: ["/listings"],
    queryFn: () => get("/listings"),
    staleTime: 1000 * 60,
    select: (data: GetResult<"/listings">) => {
      const listingsWithDiscounts = getListingsWithDiscountInformation(
        data,
        shopDiscounts?.listingDiscounts,
        customShopCategories
      );
      return listingsWithDiscounts
        .sort((a, b) => b.sellVolume - a.sellVolume)
        .map((listing) => {
          if (!listing.imageUrl || !listing.title || !listing.baseCost) {
            return null;
          }
          return {
            to: "/pdp/package/" + listing.id,
            tag: listing.type,
            title: listing.title,
            imageSrc: listing.imageUrl ?? "",
            price: listing.baseCost,
            memberPrice: listing.membershipCost,
            discount: listing.discountText,
            customColor: listing.customColor,
          };
        })
        .filter(notFalsy)
        .slice(0, 5);
    },
  });

  const title = props.widget.title;
  if (!title || !listings || !listings.length) return null;

  return (
    <ProductCarousel
      svg={props.widget.icon && ICONS[props.widget.icon]}
      title={title}
      description={props.widget.description}
      cards={listings}
    />
  );
};

const load = async (request: Request) => {
  /* When displaying the page in a mobile webview, we let the app handle displaying its own navbar */
  const showNavbar =
    new URL(request.url).searchParams.get("isMobileApp") !== "true";

  const query = new URL(request.url).searchParams;
  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );
  const layout = queryClient.fetchQuery({
    queryKey: ["/widgets", { page: "DiscoverPage" }],
    queryFn: () => get("/widgets", { page: "DiscoverPage" }),
  });

  const cartDetails = queryClient.getQueryData<
    Models<"ShopDisplayOrderResult">
  >(CURRENT_ORDER_QUERY_KEY);

  return waitObj({
    layout,
    cartDetails,
    repeatOfferId: query.get("repeatOffer"),
    widgetId: query.get("widget"),
    promotionId: query.get("promotion"),
    platform,
    showNavbar,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const discoverLoader: LoaderFunction = async ({ request }) => {
  const data = await load(request);
  return data;
};

const DiscoverPage = () => {
  const {
    layout,
    promotionId,
    repeatOfferId,
    widgetId,
    showNavbar,
    platform,
    cartDetails,
  } = useLoaderData() as LoaderData;

  const navigate = useNavigate();
  const adonisV3CTAText = useFeatureFlag("adonisV3CTAText");

  const { isPersonalisationEnabled, openChat } = useAdonisChat();
  const { preferences } = useUserPreferences();

  const [showOfferNotAppliedToCartModal, setShowOfferNotAppliedToCartModal] =
    useState(false);

  const [appliedPromotionTitle, setAppliedPromotionTitle] = useState<string>();

  const trackAdonisViewed = (): void => {
    Analytics.track(AnalyticsEvents.ADONIS_VIEWED, {});
  };

  const handleAdonisCTAClick = () => {
    openChat();
    trackAdonisViewed();
  };

  if (promotionId) {
    return (
      <>
        <PromotionInfo
          widgetId={widgetId}
          promotionId={promotionId}
          showNavbar={showNavbar}
          cartDetails={cartDetails ?? undefined}
          onNotAppliedToCartError={() =>
            setShowOfferNotAppliedToCartModal(true)
          }
          onAppliedToCart={(promotionTitle) => {
            setAppliedPromotionTitle(promotionTitle);
          }}
        />
        {showOfferNotAppliedToCartModal && (
          <ItemNotAppliedToCartModal
            onClose={() => setShowOfferNotAppliedToCartModal(false)}
            itemType="offer"
          />
        )}
        {!!appliedPromotionTitle && (
          <ItemAppliedToCartModal
            onClose={() => setAppliedPromotionTitle(undefined)}
            itemType="offer"
            itemName={appliedPromotionTitle}
          />
        )}
      </>
    );
  } else if (widgetId) {
    return <CustomWidgetInfo widgetId={widgetId} showNavbar={showNavbar} />;
  } else if (repeatOfferId) {
    return <RepeatOfferInfo showNavbar={showNavbar} />;
  }

  return (
    <>
      {showNavbar && (
        <TopNav>
          <h1 className="text-xl">Discover</h1>
        </TopNav>
      )}
      {isPersonalisationEnabled && (
        <AdvisorCTA
          ctaText={adonisV3CTAText}
          onChatClick={handleAdonisCTAClick}
          showLink={
            preferences?.concernIds && preferences.concernIds.length > 0
          }
          onLinkClick={() => navigate("/shop/recommended-treatments")}
        />
      )}
      <div className="flex flex-col [&>:nth-child(even)]:bg-two">
        {layout.map((widget) => {
          if (
            widget.type === "RepeatMDOffer" &&
            platform.platformSettings?.repeatMDMarketingEnabled === false
          ) {
            return null;
          }

          switch (widget.type) {
            case "CampaignCard":
              return (
                <DiscoverCard
                  key={widget.id}
                  widget={widget}
                  showNavbar={showNavbar}
                  marketplaceEnabled={
                    platform.platformSettings?.marketplaceEnabled
                  }
                  onPromotionNotAppliedToCartError={() =>
                    setShowOfferNotAppliedToCartModal(true)
                  }
                  onPromotionAppliedToCart={(promotionTitle) => {
                    setAppliedPromotionTitle(promotionTitle);
                  }}
                  cartDetails={cartDetails ?? undefined}
                />
              );
            case "DiscoverCarousel":
              return (
                <DiscoverCarousel
                  key={widget.id}
                  widget={widget}
                  marketplaceEnabled={
                    platform.platformSettings?.marketplaceEnabled
                  }
                />
              );
            case "ReferAFriend":
              return (
                <>
                  <ReferAFriend key={widget.id} />
                  <ReferralRewardsCard />
                </>
              );
            case "RepeatMDOffer":
              return (
                <VacationCard
                  key={widget.id}
                  widget={widget}
                  showNavbar={showNavbar}
                />
              );
            case "ExternalLink":
              return (
                <DiscoverCard
                  cartDetails={cartDetails ?? undefined}
                  key={widget.id}
                  widget={widget}
                  showNavbar={showNavbar}
                  marketplaceEnabled={
                    platform.platformSettings?.marketplaceEnabled
                  }
                />
              );
            case "TopSellingCarousel":
              if (!platform.platformSettings?.marketplaceEnabled) return null;
              return <TopSellingCarousel key={widget.id} widget={widget} />;
            default:
              return null;
          }
        })}
      </div>
      {showOfferNotAppliedToCartModal && (
        <ItemNotAppliedToCartModal
          onClose={() => setShowOfferNotAppliedToCartModal(false)}
          itemType="offer"
        />
      )}
      {!!appliedPromotionTitle && (
        <ItemAppliedToCartModal
          onClose={() => setAppliedPromotionTitle(undefined)}
          itemType="offer"
          itemName={appliedPromotionTitle}
        />
      )}
    </>
  );
};

export default DiscoverPage;
